import { SubsAndPlansActionTypes } from './constants';

type SubPlanListData = {
    plan_id: string;
    plan_name: string;
    description: string;
    price: string;
    billing_cycle: string;
    max_users?: string;
    max_outlets?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    modules: { module_id: string; module_name: string }[];
    total_price: string;
};

type SubPlanListSuccess = {
    totalPages: number;
    page: number;
    plans: SubPlanListData[];
};

type SubList = {
    business_id: string;
    outlet_id: string;
};

type SubPlanCreate = {
    plan_name: string;
    description: string;
    modules: string;
    billing_cycle: string;
    is_active: boolean;
};

type SubPlanUpdate = {
    plan_id: string;
    plan_name: string;
    description: string;
    price: string;
    billing_cycle: string;
    max_users: string;
    max_outlets: string;
};

type SubPlanUpdateStatus = {
    plan_id: string;
    is_active: boolean;
};

type SubPlanDelete = { plan_id: string };
type SubAdd = {
    business_id: string;
    outlet_id: string;
    plan_id: string;
    status: string;
    start_date: string;
    end_date: string;
};

interface SubsAndPlansState {
    loading: boolean;
    plans: SubPlanListData[]; // flat array of plans
    subscriptions: any;
    totalPages: number;
    page: number;
    error: string | null;
    successMessage: string | null;
}

const initialState: SubsAndPlansState = {
    loading: false,
    plans: [],
    subscriptions: [],
    totalPages: 0,
    page: 1,
    error: null,
    successMessage: null,
};

export type SubsAndPlansAction =
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_LIST }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_LIST_SUCCESS; payload: SubPlanListSuccess }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_LIST_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_DELETE; payload: SubPlanDelete }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_DELETE_SUCCESS; payload: { message: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_DELETE_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_ADD; payload: SubPlanCreate }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_ADD_SUCCESS; payload: { message: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_ADD_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_LIST; payload: SubList }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_LIST_SUCCESS; payload: any }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_LIST_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE; payload: SubPlanUpdate }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_SUCCESS; payload: { message: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_STATUS; payload: SubPlanUpdateStatus }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_STATUS_SUCCESS; payload: { message: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_STATUS_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_ADD; payload: SubAdd }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_ADD_SUCCESS; payload: { message: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_ADD_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_SCHEDULE; payload: any }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_SCHEDULE_SUCCESS; payload: { message: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_SCHEDULE_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_ADD_MODULES; payload: any }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_ADD_MODULES_SUCCESS; payload: { message: string } }
    | { type: typeof SubsAndPlansActionTypes.SUBSCRIPTION_ADD_MODULES_ERROR; payload: { error: string } }
    | { type: typeof SubsAndPlansActionTypes.RESET_SUB_SUCCESS };

const subsAndPlansReducer = (state = initialState, action: SubsAndPlansAction): SubsAndPlansState => {
    switch (action.type) {
        // 🔹 Loading
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_LIST:
        case SubsAndPlansActionTypes.SUBSCRIPTION_LIST:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_DELETE:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_ADD:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_STATUS:
        case SubsAndPlansActionTypes.SUBSCRIPTION_ADD:
        case SubsAndPlansActionTypes.SUBSCRIPTION_ADD_MODULES:
        case SubsAndPlansActionTypes.SUBSCRIPTION_SCHEDULE:
            return {
                ...state,
                loading: true,
                error: null,
                successMessage: null,
            };

        // 🔹 List Success with pagination
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                plans: action.payload.plans, // flat array
                totalPages: action.payload.totalPages,
                page: action.payload.page,
                error: null,
            };

        case SubsAndPlansActionTypes.SUBSCRIPTION_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                subscriptions: action.payload.data, // flat array
                // totalPages: action.payload.totalPages,
                // page: action.payload.page,
                error: null,
            };

        // 🔹 Errors
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_LIST_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_LIST_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_DELETE_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_SCHEDULE_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_ADD_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_STATUS_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_ADD_ERROR:
        case SubsAndPlansActionTypes.SUBSCRIPTION_ADD_MODULES_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };

        // 🔹 Success messages
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_DELETE_SUCCESS:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_ADD_SUCCESS:
        case SubsAndPlansActionTypes.SUBSCRIPTION_SCHEDULE_SUCCESS:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_SUCCESS:
        case SubsAndPlansActionTypes.SUBSCRIPTION_PLAN_UPDATE_STATUS_SUCCESS:
        case SubsAndPlansActionTypes.SUBSCRIPTION_ADD_SUCCESS:
        case SubsAndPlansActionTypes.SUBSCRIPTION_ADD_MODULES_SUCCESS:
            return {
                ...state,
                loading: false,
                successMessage: action.payload.message,
                error: null,
            };

        case SubsAndPlansActionTypes.RESET_SUB_SUCCESS:
            return {
                ...state,
                successMessage: '',
            };

        default:
            return state;
    }
};

export default subsAndPlansReducer;
